import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import ApiClient from '../../services/apiClient/ApiClient'
import type {
  LandFavorite,
  CreateLandFavoriteRequest,
  LandFavoritesState,
} from '@popety-io/types'

const apiClient = new ApiClient()

// Async thunks for API calls
export const getAllLandFavorites = createAsyncThunk(
  'favorites/getAllLandFavorites',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/favorites/land')
      if (response.errors) {
        return rejectWithValue(response.errors[0])
      }
      return response.data as LandFavorite[]
    } catch {
      return rejectWithValue({ message: 'Failed to fetch favorites' })
    }
  }
)

export const createLandFavorite = createAsyncThunk(
  'favorites/createLandFavorite',
  async (favoriteData: CreateLandFavoriteRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/favorites/land', favoriteData)
      if (response.errors) {
        return rejectWithValue(response.errors[0])
      }
      return response.data as LandFavorite
    } catch {
      return rejectWithValue({ message: 'Failed to create favorite' })
    }
  }
)

export const getLandFavoriteById = createAsyncThunk(
  'favorites/getLandFavoriteById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/favorites/land/${id}`)
      if (response.errors) {
        return rejectWithValue(response.errors[0])
      }
      return response.data as LandFavorite
    } catch {
      return rejectWithValue({ message: 'Failed to fetch favorite' })
    }
  }
)

export const getLandFavoritesByLandId = createAsyncThunk(
  'favorites/getLandFavoritesByLandId',
  async (landId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/favorites/land/by-land/${landId}`)
      if (response.errors) {
        return rejectWithValue(response.errors[0])
      }
      return response.data as LandFavorite[]
    } catch {
      return rejectWithValue({ message: 'Failed to fetch favorites by land' })
    }
  }
)

export const deleteLandFavorite = createAsyncThunk(
  'favorites/deleteLandFavorite',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(`/favorites/land/${id}`)
      if (response.errors) {
        return rejectWithValue(response.errors[0])
      }
      return id
    } catch {
      return rejectWithValue({ message: 'Failed to delete favorite' })
    }
  }
)

const initialState: LandFavoritesState = {
  favorites: [],
  loading: false,
  error: null,
}

const favoritesSlice = createSlice({
  name: 'favorites',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearFavorites: (state) => {
      state.favorites = []
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all favorites
      .addCase(getAllLandFavorites.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getAllLandFavorites.fulfilled,
        (state, action: PayloadAction<LandFavorite[]>) => {
          state.loading = false
          state.favorites = action.payload
        }
      )
      .addCase(getAllLandFavorites.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to fetch favorites'
      })

      // Create favorite
      .addCase(createLandFavorite.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        createLandFavorite.fulfilled,
        (state, action: PayloadAction<LandFavorite>) => {
          state.loading = false
          state.favorites.push(action.payload)
        }
      )
      .addCase(createLandFavorite.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to create favorite'
      })

      // Get favorite by ID
      .addCase(getLandFavoriteById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getLandFavoriteById.fulfilled,
        (state, action: PayloadAction<LandFavorite>) => {
          state.loading = false
          const existingIndex = state.favorites.findIndex(
            (fav) => fav.id === action.payload.id
          )
          if (existingIndex !== -1) {
            state.favorites[existingIndex] = action.payload
          } else {
            state.favorites.push(action.payload)
          }
        }
      )
      .addCase(getLandFavoriteById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to fetch favorite'
      })

      // Get favorites by land ID
      .addCase(getLandFavoritesByLandId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getLandFavoritesByLandId.fulfilled,
        (state, action: PayloadAction<LandFavorite[]>) => {
          state.loading = false
          // Update existing favorites or add new ones
          action.payload.forEach((favorite) => {
            const existingIndex = state.favorites.findIndex(
              (fav) => fav.id === favorite.id
            )
            if (existingIndex !== -1) {
              state.favorites[existingIndex] = favorite
            } else {
              state.favorites.push(favorite)
            }
          })
        }
      )
      .addCase(getLandFavoritesByLandId.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload?.message || 'Failed to fetch favorites by land'
      })

      // Delete favorite
      .addCase(deleteLandFavorite.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        deleteLandFavorite.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false
          state.favorites = state.favorites.filter(
            (fav) => fav.id !== action.payload
          )
        }
      )
      .addCase(deleteLandFavorite.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to delete favorite'
      })
  },
})

export const { clearError, clearFavorites } = favoritesSlice.actions

export const favoritesReducer = { favorites: favoritesSlice.reducer }
